// Libraries
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';

// MUI
import { Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';

// Core
import {
    ParameterValues,
    paramRepository,
    ResourceTextApplication,
} from '../../../../core/resources';
import FormValidator from '../../../../core/FormValidator';
import utils from '../../../../core/utils';
import defaultData from '../../../../core/defaultData';

// Common
import {
    ADialog,
    ADialogActions,
    ADialogButtonsProp,
    ADialogContent,
    ADialogTitle,
} from '../../../../common/dialog';
import {
    ALabel,
    ADropdown,
    AInputContainer,
    ANumberInput,
    ATextInput,
    AAutocomplete,
    AAutocompleteValue,
    AInputItem,
} from '../../../../common/inputs';
import { Context } from '../../../../common/contextSelector/ContextSelector';
import ConfirmDialogClose from '../../../../common/dialog/ConfirmDialogClose';

// Interfaces
import {
    AliasTypeEnum,
    AnnouncementFlagsEnum,
    AnnouncementTypeEnum,
    DialogModeEnum,
    FarmingTypeEnum,
} from '../../../../interfaces/enums';
import {
    IAnimalAnnouncement,
    ICollectWeek,
    ICreateBovinePurchaseAnnouncement,
    IUpdateAnnouncementResult,
    IUpdateBovinePurchaseAnnouncement,
    IUpdatePreliminaryBovinePurchaceAnnouncement,
} from '../../../../interfaces/IAnnouncement';
import { IFarmDetails } from '../../../../interfaces/IBusinessEntities';
import { IApiResultMessage, ITiltuApiResult } from '../../../../interfaces/IGeneral';

// Store
import { useAppSelector } from '../../../../store/hooks';

// API
import companyApi from '../../../../api/companyApi';
import AnimalAnnouncementApi from '../../../../api/animalAnnouncementApi';
import notificationApi from '../../../../api/notificationApi';

// Feature
import {
    generateCollectWeekSequence,
    initializeCollectWeekStart,
    mapCollectWeeksForDropdown,
} from '../../helpers/date';
import { mapParameters } from '../../../shared/helpers/data';
import { Strings } from '../../../shared/constants/strings';
import { getFarmingTypesFromFarmDetails, hasAnnouncementResponseErrors } from '../../helpers/data';
import {
    collectWeekStartNotValid,
    collectWeekStartHasValue,
    collectWeekEndHasValue,
    collectWeekEndNotValid,
} from '../../helpers/collectWeek';
import { getHoldingSiteItems } from '../../helpers/holdingSite';
import {
    constructAnnouncementNumberGridSize,
    constructCollectWeekGridSize,
} from '../../../shared/helpers/gridSize';
import { IGridSize } from '../bovine-slaughter-announcement/BovineSlaughterAnnouncement';
import { holdingSiteHasValue } from '../../../shared/helpers/utils';
import { boundaryRepository } from '../../../../core/boundaries';
import auth from '../../../../core/authorization';
import '../../styles/announcementStyles.scss';
import { filterSalesTypeParameter } from '../../helpers/salesType';
import { isNotNil } from 'ramda';
import ConfirmDialog from '../../../shared/components/confirm/ConfirmDialog';
import { GUIDType } from '../../../../interfaces/types';

export interface IAnnouncementDialogProps {
    data: IAnimalAnnouncement | null;
    onClose: () => void;
    mode: DialogModeEnum;
    processAnnouncementSave: (
        data: IUpdateAnnouncementResult,
        type: AnnouncementTypeEnum,
        holdingSiteId: string
    ) => void;
    processAnnouncementDelete: (announcementId: GUIDType) => void;
    announcementConfirmation?: boolean;
    readonly?: boolean;
    weekReadonly?: boolean;
}

export default function BovinePurchaseAnnouncement(props: IAnnouncementDialogProps) {
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaBovine',
        'AnelmaGeneral',
        'AnelmaCompany',
        'AnelmaAnimalAnnouncement',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const animalAnnouncementType = AnnouncementTypeEnum.BovinePurchase;
    const context = useAppSelector((state) => state.context);
    const validator = new FormValidator();
    const userData = useAppSelector((state) => state.userData.data);

    const initializeStartWeek = useCallback(
        () =>
            props.data?.Batches.find((i) => i.CollectWeekStart)?.CollectWeekStart ??
            initializeCollectWeekStart(animalAnnouncementType),
        []
    );

    const [isLoading, setIsLoading] = useState<boolean>();
    const [farmDetails, setFarmDetails] = useState<IFarmDetails>();
    const [producerNumber, setProducerNumber] = useState<string>('');
    const [holdingSiteId, setHoldingSiteId] = useState<string>('');
    const [collectWeekStart, setCollectWeekStart] = useState<ICollectWeek>(initializeStartWeek());
    const [collectWeekEnd, setCollectWeekEnd] = useState<ICollectWeek>(
        props.data?.Batches.find((i) => i.CollectWeekEnd)?.CollectWeekEnd ?? {
            Year: -1,
            Week: -1,
        }
    );

    const [collectWeekDay2, setCollectWeekDay2] = useState(
        props.data?.Batches.find((i) => i.CollectWeekDay)?.CollectWeekDay ?? ''
    );
    const [collectWeekDayParams, setCollectWeekDayParams] = useState<ParameterValues>([]);
    const [collectWeeks, setCollectWeeks] = useState<ICollectWeek[]>([]);

    const [announcementInfo, setAnnouncementInfo] = useState(props.data?.Batches[0].BatchInfo);

    const [animalCount, setAnimalCount] = useState<number>(
        (props.data?.Batches.find((i) => i.Amount)?.Amount as number) ?? 0
    );

    const [salesTypeParameterCode, setSalesTypeParameterCode] = useState(
        props.data?.Batches[0].SalesType !== undefined && props.data?.Batches[0].SalesType !== null
            ? props.data?.Batches[0].SalesType
            : -1
    );

    const [salesTypeParams, setSalesTypeParams] = useState<ParameterValues>([]);
    const [combinedSalesTypeParams, setCombinedSalesTypeParams] = useState<ParameterValues>([]);
    const [breederBrokerPurchaseParams, setBreederBrokerPurchaseParams] = useState<ParameterValues>(
        []
    );
    const [rearingUnitBrokerPurchaseParams, setRearingUnitBrokerPurchaseParams] =
        useState<ParameterValues>([]);
    const [sucklerCowCattleBrokerPurchaseParams, setSucklerCowCattleBrokerPurchaseParams] =
        useState<ParameterValues>([]);

    const [createBovinePurchaseAnnoucement, setCreateBovinePurchaseAnnoucement] =
        useState<ICreateBovinePurchaseAnnouncement>(defaultData.animalAnnoucementBoviePurchase());
    const [holdingSiteItems, setHoldingSiteItems] = useState<AInputItem<AAutocompleteValue>[]>([]);
    const [collectWeekGridSize, setCollectWeekGridSize] = useState<IGridSize>({
        Xs: 2,
        Sm: 2,
        Md: 2,
        Lg: 2,
    });
    const [announcementNumberGridSize, setAnnouncementNumberGridSize] = useState<IGridSize>({
        Xs: 4,
        Sm: 4,
        Md: 4,
        Lg: 4,
    });
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);

    const [addInfoMinLength, setAddInfoMinLength] = useState<number | undefined>();
    const [addInfoMaxLength, setAddInfoMaxLength] = useState<number | undefined>();
    const [boundariesLoaded, setBoundariesLoaded] = useState<boolean>(false);
    const [displayRejectDialog, setDisplayRejectDialog] = useState<boolean>(false);

    const theme = useTheme();
    const isBelowMdDevice = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        initializeCollectWeeks(initializeCollectWeekStart(animalAnnouncementType).Week);
        getCollectWeekDayParams();
        getSalesTypeParameters();

        boundaryRepository.load(['AnelmaAnimalAnnouncement']).then(() => {
            const boundaries = boundaryRepository.resource(
                'AnelmaAnimalAnnouncement',
                'BovinePurchaseAnnouncementInfo'
            );

            if (boundaries) {
                setAddInfoMaxLength(
                    Number.isInteger(boundaries.maxValue)
                        ? (boundaries.maxValue as number)
                        : undefined
                );
                setAddInfoMinLength(
                    Number.isInteger(boundaries.minValue)
                        ? (boundaries.minValue as number)
                        : undefined
                );
            }

            setBoundariesLoaded(true);
        });
    }, []);

    useEffect(() => {
        if (context.data.currentContext) {
            initializeFarmDetails(context.data.currentContext);
        } else setFarmDetails(undefined);
    }, [context]);

    useEffect(() => {
        if (combinedSalesTypeParams.length > 0) {
            setSalesTypeParams(
                combinedSalesTypeParams.filter(
                    (value, idx, self) =>
                        idx ===
                        self.findIndex((t) => t.code === value.code && t.text === value.text)
                )
            );
        }
    }, [combinedSalesTypeParams]);

    useEffect(() => {
        if (farmDetails !== undefined) {
            combineSalesTypeParams(farmDetails);
            setHoldingSiteItems(getHoldingSiteItems(farmDetails));
        } else if (props.mode === DialogModeEnum.Edit && props.data?.HoldingSiteId)
            setHoldingSiteId(props.data.HoldingSiteId);
    }, [farmDetails]);

    useEffect(() => {
        if (collectWeekStartNotValid(collectWeekStart, collectWeekEnd)) {
            if (
                collectWeekStartHasValue(collectWeekStart) &&
                collectWeekEndHasValue(collectWeekEnd)
            ) {
                enqueueSnackbar(t('AnelmaAnimalAnnouncement:1006'), {
                    variant: 'error',
                });
                setCollectWeekStart({ Year: -1, Week: -1 });
            }
        }
    }, [collectWeekStart]);

    useEffect(() => {
        if (collectWeekEndNotValid(collectWeekEnd, collectWeekStart)) {
            if (collectWeekEndHasValue(collectWeekEnd)) {
                enqueueSnackbar(t('AnelmaAnimalAnnouncement:1007'), {
                    variant: 'error',
                });
                setCollectWeekEnd({ Year: -1, Week: -1 });
            }
        }
    }, [collectWeekEnd]);

    useEffect(() => {
        if (isBelowMdDevice) {
            setAnnouncementNumberGridSize(constructAnnouncementNumberGridSize());
            setCollectWeekGridSize(constructCollectWeekGridSize());
        }
    }, [isBelowMdDevice]);

    // Generate collect week sequences for the current and next year
    const initializeCollectWeeks = (from: number) => {
        var val: ICollectWeek[] = [];

        val = val.concat(
            generateCollectWeekSequence(
                from,
                moment().isoWeeksInYear() - from,
                moment().isoWeekYear()
            ));

        val = val.concat(
            generateCollectWeekSequence(
                1,
                from - 2,
                moment().isoWeekYear() + 1
            )
        )

        setCollectWeeks(val);
    };

    const getCollectWeekDayParams = () => {
        paramRepository.load(['AnelmaAnimalAnnouncement']).then(() => {
            setCollectWeekDayParams(
                paramRepository.resource('AnelmaAnimalAnnouncement', 'CollectWeekDay')
            );
        });
    };

    const getSalesTypeParameters = () => {
        paramRepository.load(['AnelmaBovine']).then(() => {
            setBreederBrokerPurchaseParams(
                filterSalesTypeParameter(
                    paramRepository.resource('AnelmaBovine', 'BreederBrokerPurchase')
                )
            );
            setRearingUnitBrokerPurchaseParams(
                filterSalesTypeParameter(
                    paramRepository.resource('AnelmaBovine', 'RearingUnitBrokerPurchase')
                )
            );
            setSucklerCowCattleBrokerPurchaseParams(
                filterSalesTypeParameter(
                    paramRepository.resource('AnelmaBovine', 'SucklerCowCattleBrokerPurchase')
                )
            );
        });
    };

    const combineSalesTypeParams = (farmDetails: IFarmDetails) => {
        // •	Kasvattaja = BreederBrokerPurchase ,            3 = kasvattaja       Upbringing
        // •	Välikasvattamo = RearingUnitBrokerPurchase,     4 = välikasvattamo   Transmission
        // •	Lypsykarjatila = N/A,                           2 = lypsykarjatila   Dairy
        // •	Emolehmätila = SucklerCowCattleBrokerPurchase,  1 = emolehmätila     Calving
        // Jos tilalla on useampi tuotantosuunta, listaan yhdistetään tilan tuotantosuuntien parametrit
        const farmingTypesInCurrentFarm = getFarmingTypesFromFarmDetails(
            farmDetails.ProductionLines
        );
        for (const farmingType of farmingTypesInCurrentFarm) {
            switch (farmingType) {
                case FarmingTypeEnum.Calving: // Emolehmätila
                    setCombinedSalesTypeParams((previousState) => [
                        ...previousState,
                        ...sucklerCowCattleBrokerPurchaseParams,
                    ]);
                    break;
                case FarmingTypeEnum.Dairy: // Lypsykarjatila
                    break;
                case FarmingTypeEnum.Upbringing: // Kasvattaja
                    setCombinedSalesTypeParams((previousState) => [
                        ...previousState,
                        ...breederBrokerPurchaseParams,
                    ]);
                    break;
                case FarmingTypeEnum.Transmission: // Välikasvattamo
                    setCombinedSalesTypeParams((previousState) => [
                        ...previousState,
                        ...rearingUnitBrokerPurchaseParams,
                    ]);
                    break;
                default:
                    break;
            }
        }
    };

    const initializeFarmDetails = (context: Context) => {
        setIsLoading(true);
        const companyByContext = companyApi
            .getCompanyByContext(context.context)
            .then((response) => {
                setFarmDetails(response?.Entity as IFarmDetails);
            });
        Promise.all([companyByContext]).then(() => {
            setIsLoading(false);
        });
        let producerId = utils.context.parseFarmContext(context.context)?.producerId;
        if (producerId) setProducerNumber(producerId);
    };

    const handleDialogClose = () => setConfirmDialogVisible(true);

    const canSave = () => {
        if (props.mode === DialogModeEnum.Create)
            return (
                !validator.invalid &&
                holdingSiteHasValue(holdingSiteId) &&
                animalCount > 0 &&
                salesTypeParameterCode !== -1 &&
                collectWeekEndHasValue(collectWeekEnd) &&
                collectWeekStartHasValue(collectWeekStart)
            );
        return true;
    };

    /** Get AnnouncementFlagsEnum value when trying to update. For Snellman user use SnellmanConfirmed and other user(s) ProducerConfirmed.
     *
     *  Default to unknown as the Deleted value isn't set via the update endpoint. If that at somepoint changes, then modify accordingly.
     *
     * @returns
     */
    const getAnnouncementFlags = (rejected: boolean) => {
        if (getBooleanValue(props.announcementConfirmation)) {
            if (rejected) {
                return AnnouncementFlagsEnum.Deleted;
            } else if (userData.IsSnellmanUser) {
                return AnnouncementFlagsEnum.SnellmanConfirmed;
            } else {
                return AnnouncementFlagsEnum.ProducerConfirmed;
            }
        }
        return AnnouncementFlagsEnum.Unknown;
    };

    const getDialogTitle = () => {
        switch (props.mode) {
            case DialogModeEnum.Create:
                return <ADialogTitle>{t('AnelmaBovine:1058')}</ADialogTitle>;
            case DialogModeEnum.Edit:
                if (getBooleanValue(props.announcementConfirmation)) {
                    return <ADialogTitle>{t('AnelmaBovine:1208')}</ADialogTitle>;
                }
                return <ADialogTitle>{t('AnelmaBovine:1059')}</ADialogTitle>;
            default:
                break;
        }
    };

    /** Check if parameter has value and whether true or not. */
    const getBooleanValue = (value?: boolean) => {
        if (isNotNil(value) && value) return true;
        else return false;
    };

    const rejectPreliminaryAnnouncement = (rejected: boolean) => {
        if (props.data && rejected) {
            AnimalAnnouncementApi
                .rejectPreliminaryAnnouncement(props.data.Id)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        });

                        return;
                    }

                    enqueueSnackbar(t('AnelmaGeneral:1018'), {
                        variant: 'success',
                    });

                    props.processAnnouncementDelete(props.data?.Id ?? "");
                    props.onClose();
                })
                .catch((error: IApiResultMessage[]) => {
                    if (error) {
                        error.forEach((e) =>
                            enqueueSnackbar(e?.Explanation ?? e, {
                                variant: 'error',
                            })
                        );
                    }
                });
        }
    };

    const onSave = (createBovinePurchaseAnnoucement: ICreateBovinePurchaseAnnouncement) => {
        //Drop down validation if not selected for salestype & collectweekday
        validator.setState(
            'announcement-sales-type-dropdown',
            salesTypeParameterCode > 0,
            (valid) => {
                if (!valid) {
                    return;
                }
            }
        );

        if (validator.invalid) {
            enqueueSnackbar(t('AnelmaGeneral:1030'), {
                variant: 'error',
            });
            setIsLoading(false);
            return;
        }

        if (props.mode === DialogModeEnum.Create) {
            {
                const newdata: ICreateBovinePurchaseAnnouncement = {
                    ...createBovinePurchaseAnnoucement,
                    ProducerNumber: Number(producerNumber),
                    AnnouncementInfo: (announcementInfo || ' ') as string,
                    FarmId: farmDetails?.Id as string,
                    BovineInfos: [
                        {
                            HoldingSiteId: holdingSiteId,
                            SalesType: salesTypeParameterCode,
                            AnimalCount: animalCount,
                            CollectWeekStart: {
                                Year: collectWeekStart.Year,
                                Week: collectWeekStart.Week,
                            },
                            CollectWeekEnd: {
                                Year: collectWeekEnd.Year,
                                Week: collectWeekEnd.Week,
                            },
                            CollectWeekDay: 3,
                            AddInfo: (announcementInfo || '') as string,
                        },
                    ],
                };
                setIsLoading(true);
                AnimalAnnouncementApi.createBovinePurchaseAnnouncement(newdata)
                    .then((response) => {
                        if (!response) {
                            enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                                variant: 'error',
                            });
                            return;
                        }
                        if (hasAnnouncementResponseErrors(response)) {
                            response.Summary.Errors.map((_) => {
                                enqueueSnackbar(`${_.Explanation}`, {
                                    variant: 'error',
                                });
                            });
                            return;
                        } else if (!hasAnnouncementResponseErrors(response)) {
                            enqueueSnackbar(t('AnelmaGeneral:1018'), {
                                variant: 'success',
                            });
                            props.processAnnouncementSave(
                                response.Entity,
                                AnnouncementTypeEnum.BovinePurchase,
                                holdingSiteId
                            );
                        }
                        props.onClose();
                    })
                    .catch((err) => {
                        enqueueSnackbar(t('AnelmaGeneral:1030'), {
                            variant: 'error',
                        });
                    })
                    .finally(() => {
                        setIsLoading(false);
                    });
            }
        } else if (props.mode === DialogModeEnum.Edit) {
            const editData: IUpdateBovinePurchaseAnnouncement = {
                AnnouncementNumber: Number(props.data?.Number),
                ProducerNumber: farmDetails?.Aliases.find(
                    (i) => i.Type === AliasTypeEnum.ProducerNumber
                )?.Value as unknown as number,
                AnnouncementInfo: announcementInfo || ' ',
                FarmId: farmDetails?.Id as string,
                Additions: [],
                Updates: [
                    {
                        AnnouncementBatchNumber: String(
                            props.data?.Batches.find((i) => i.BatchNumber)?.BatchNumber
                        ),
                        SalesType: salesTypeParameterCode,
                        AnimalCount: animalCount,
                        CollectWeekStart: {
                            Year: collectWeekStart.Year,
                            Week: collectWeekStart.Week,
                        },
                        CollectWeekEnd: {
                            Year: collectWeekEnd.Year,
                            Week: collectWeekEnd.Week,
                        },
                        CollectWeekDay: collectWeekDay2 as number,
                        AddInfo: announcementInfo || ' ',
                    },
                ],
                Flags: getAnnouncementFlags(false),
            };

            setIsLoading(true);
            AnimalAnnouncementApi.updateBovinePurchaseAnnouncement(editData)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                            variant: 'error',
                        });
                        return;
                    }
                    if (hasAnnouncementResponseErrors(response)) {
                        response.Summary.Errors.map((_) => {
                            enqueueSnackbar(`${_.Explanation}`, {
                                variant: 'error',
                            });
                        });
                        return;
                    } else if (!hasAnnouncementResponseErrors(response)) {
                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });
                        props.processAnnouncementSave(
                            response.Entity,
                            AnnouncementTypeEnum.BovinePurchase,
                            holdingSiteId
                        );
                        props.onClose();
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(t('AnelmaGeneral:1030'), {
                        variant: 'error',
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const updatePreliminaryBovinePurchaseAnnouncement = (flags: AnnouncementFlagsEnum) => {
        if (props.mode === DialogModeEnum.Edit && props.data) {
            const editData: IUpdatePreliminaryBovinePurchaceAnnouncement = {
                AnnouncementId: props.data?.Id,
                ProducerNumber: farmDetails?.Aliases.find(
                    (i) => i.Type === AliasTypeEnum.ProducerNumber
                )?.Value as unknown as number,
                AnnouncementInfo: announcementInfo || ' ',
                FarmId: farmDetails?.Id as string,
                Additions: [],
                Updates: [
                    {
                        Id: props.data?.Batches.find((i) => i.Id)?.Id ?? "",
                        AnnouncementBatchNumber: String(
                            props.data?.Batches.find((i) => i.BatchNumber)?.BatchNumber ?? "1"
                        ),
                        SalesType: salesTypeParameterCode,
                        AnimalCount: animalCount,
                        CollectWeekStart: {
                            Year: collectWeekStart.Year,
                            Week: collectWeekStart.Week,
                        },
                        CollectWeekEnd: {
                            Year: collectWeekEnd.Year,
                            Week: collectWeekEnd.Week,
                        },
                        CollectWeekDay: collectWeekDay2 as number,
                        AddInfo: announcementInfo || ' ',
                    },
                ],
                Flags: flags,
            };

            setIsLoading(true);
            AnimalAnnouncementApi.updatePreliminaryBovinePurchaseAnnouncement(editData)
                .then((response) => {
                    if (!response) {
                        enqueueSnackbar(`${t('AnelmaGeneral:1019')}`, {
                            variant: 'error',
                        });
                        return;
                    }
                    if (hasAnnouncementResponseErrors(response)) {
                        response.Summary.Errors.map((_) => {
                            enqueueSnackbar(`${_.Explanation}`, {
                                variant: 'error',
                            });
                        });
                        return;
                    } else if (!hasAnnouncementResponseErrors(response)) {
                        enqueueSnackbar(t('AnelmaGeneral:1018'), {
                            variant: 'success',
                        });
                        props.processAnnouncementSave(
                            response.Entity,
                            AnnouncementTypeEnum.BovinePurchase,
                            holdingSiteId
                        );
                        props.onClose();
                    }
                })
                .catch((err) => {
                    enqueueSnackbar(t('AnelmaGeneral:1030'), {
                        variant: 'error',
                    });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    };

    const sendPreliminaryAnnouncementNotification = (announcementId: GUIDType) => {
        notificationApi
            .sendPreliminaryAnnouncementNotification(announcementId)
            .then((response) => {
                if (!response)
                    enqueueSnackbar(t('AnelmaGeneral:1019'), {
                        variant: 'error',
                    });

                enqueueSnackbar(t('AnelmaBovine:1236'), {
                    variant: 'success',
                });
            })
            .catch((error: IApiResultMessage[]) => {
                if (error) {
                    error.forEach((e) =>
                        enqueueSnackbar(e?.Explanation ?? t('AnelmaGeneral:1019'), {
                            variant: 'error',
                        })
                    );
                }
            });
    };

    const dialogActionButtons: ADialogButtonsProp = {
        left: [
            {
                type: 'cancel',
                onClick: () => handleDialogClose(),
            },
        ],
        right: getBooleanValue(props.announcementConfirmation)
            ? [
                  {
                      onClick: () => setDisplayRejectDialog(true),
                      type: 'default',
                      disabled: isLoading === true || !canSave() || props.readonly,
                      label: t('AnelmaBovine:1201'),
                  },
                  {
                      onClick: () =>
                          updatePreliminaryBovinePurchaseAnnouncement(AnnouncementFlagsEnum.ProducerConfirmed),
                      type: 'ok',
                      disabled: isLoading === true || !canSave() || props.readonly,
                      label: t('AnelmaBovine:1200'),
                  },
              ]
            : [
                  {
                      onClick: () => {
                          onSave(createBovinePurchaseAnnoucement);
                      },
                      type: 'save',
                      disabled: isLoading === true || !canSave() || props.readonly,
                  },
              ],
    };

    if (
        auth.canViewAdminContextSelector &&
        props.data?.Type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
    ) {
        dialogActionButtons.left.push({
            label: t('AnelmaBovine:1228'),
            onClick: () => sendPreliminaryAnnouncementNotification(props.data!.Id),
            type: 'default',
        });
    }

    const getAnnouncementNumber = (announcementData: IAnimalAnnouncement | null) => {
        return announcementData && announcementData?.Number > 0
            ? `${announcementData.Number}`
            : ( announcementData
                && announcementData.Type === AnnouncementTypeEnum.PreliminaryBovinePurchaseAnnouncement
                && announcementData.PreliminaryNumber != null )
                ? `${announcementData.PreliminaryNumber}`
                : '';
    }

    return (
        <ADialog open onClose={props.onClose} disableEnforceFocus>
            {getDialogTitle()}

            <ADialogContent size={'lg'} isLoading={isLoading}>
                <Grid container direction='row'>
                    {props.mode === DialogModeEnum.Edit ? (
                        <Grid size={12} style={{ marginBottom: '1.5em' }}>
                            <AInputContainer>
                                <Typography variant='h5'>
                                    {`${t('AnelmaBovine:1052').toUpperCase()} ${getAnnouncementNumber(props.data)}`}
                                </Typography>
                            </AInputContainer>
                        </Grid>
                    ) : null}
                    <Grid container direction='row' size={12}>
                        <Grid
                            size={{
                                xs: announcementNumberGridSize.Xs,
                                sm: announcementNumberGridSize.Sm,
                                md: announcementNumberGridSize.Md,
                                lg: announcementNumberGridSize.Lg
                            }}>
                            <ALabel className='anelma-animal-announcement-general-label'>
                                {t('AnelmaBovine:1165')}
                            </ALabel>
                        </Grid>
                        {props.mode === DialogModeEnum.Create && holdingSiteItems.length > 0 ? (
                            <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}>
                                <AAutocomplete
                                    id={Strings.AnnouncementHoldingSiteId}
                                    items={holdingSiteItems}
                                    label={t('AnelmaBovine:1063')}
                                    onChange={(_) => setHoldingSiteId(_?.toString() ?? '')}
                                    validator={validator}
                                    required
                                    value={holdingSiteId}
                                    error={!(holdingSiteId?.length > 0)}
                                />
                            </Grid>
                        ) : (
                            <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4 }}>
                                <ATextInput
                                    id={Strings.AnnouncementHoldingSiteId}
                                    label={t('AnelmaBovine:1063')}
                                    onChange={(v) => false}
                                    validator={validator}
                                    disabled
                                    value={holdingSiteId}
                                    lengthBoundaryName='AnelmaCompany:HoldingNumberLength'
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Grid
                        size={{
                            xs: collectWeekGridSize.Xs,
                            sm: collectWeekGridSize.Sm,
                            md: collectWeekGridSize.Md,
                            lg: collectWeekGridSize.Lg
                        }}>
                        <ADropdown
                            id={Strings.AnnouncementCollectWeekStartNumber}
                            onChange={(v) =>
                                setCollectWeekStart(JSON.parse(v as string) as ICollectWeek)
                            }
                            value={JSON.stringify(
                                collectWeekStart.Week === -1 && collectWeekStart.Year === -1
                                    ? { Year: '', Week: '' }
                                    : collectWeekStart
                            )}
                            items={mapCollectWeeksForDropdown(collectWeeks)}
                            validator={validator}
                            required
                            error={!collectWeekStartHasValue(collectWeekStart)}
                            disabled={props.readonly || props.weekReadonly}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: collectWeekGridSize.Xs,
                            sm: collectWeekGridSize.Sm,
                            md: collectWeekGridSize.Md,
                            lg: collectWeekGridSize.Lg
                        }}>
                        <ADropdown
                            id={Strings.AnnouncementCollectWeekEndNumber}
                            onChange={(v) =>
                                setCollectWeekEnd(JSON.parse(v as string) as ICollectWeek)
                            }
                            value={JSON.stringify(
                                collectWeekEnd.Week === -1 && collectWeekEnd.Year === -1
                                    ? { Year: '', Week: '' }
                                    : collectWeekEnd
                            )}
                            items={mapCollectWeeksForDropdown(collectWeeks)}
                            validator={validator}
                            error={!collectWeekEndHasValue(collectWeekEnd)}
                            disabled={props.readonly || props.weekReadonly}
                        />
                    </Grid>
                    <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8 }}>
                        <ANumberInput
                            id={Strings.AnnouncementAnimalCount}
                            label={t('AnelmaBovine:1041')}
                            mode='int'
                            onChange={(v) => setAnimalCount(v as number)}
                            value={animalCount === 0 ? null : animalCount}
                            validator={validator}
                            min={1}
                            required
                            error={!(animalCount > 0)}
                            disabled={props.readonly}
                        />
                    </Grid>
                    <Grid container size={12}>
                        <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                            <ADropdown
                                label={t('AnelmaBovine:1019')}
                                id={Strings.AnnouncementSalesTypeDropdown}
                                items={mapParameters(salesTypeParams)}
                                onChange={(v) => setSalesTypeParameterCode(v as number)}
                                validator={validator}
                                value={salesTypeParameterCode === -1 ? '' : salesTypeParameterCode}
                                required
                                error={salesTypeParameterCode === -1}
                                disabled={props.readonly}
                            />
                        </Grid>
                        {boundariesLoaded && (
                            <Grid size={{ xs: 12, sm: 12, md: 10, lg: 6 }}>
                                <ATextInput
                                    id={Strings.AnnouncementAdditionalInformation}
                                    label={t('AnelmaBovine:1039')}
                                    onChange={(v) => setAnnouncementInfo(v)}
                                    value={announcementInfo as string}
                                    validator={validator}
                                    maxLength={addInfoMaxLength}
                                    minLength={addInfoMinLength}
                                    disabled={props.readonly}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </ADialogContent>

            <ADialogActions buttons={dialogActionButtons} />

            {confirmDialogVisible && (
                <ConfirmDialogClose
                    confirmation='AnelmaGeneral:1065'
                    onCancel={() => setConfirmDialogVisible(false)}
                    onClose={() => props.onClose()}
                    open={confirmDialogVisible}
                />
            )}

            {displayRejectDialog && (
                <ConfirmDialog
                    data-robot-id='bovine-purchase-announcement-reject-dialog'
                    title={'AnelmaBovine:1201'}
                    content={'AnelmaBovine:1209'}
                    actionButtonType={'ok'}
                    onClose={() => setDisplayRejectDialog(false)}
                    onConfirm={() => rejectPreliminaryAnnouncement(true)}
                />
            )}
        </ADialog>
    );
}
